import React, { useEffect, useRef } from 'react';
import { pick, keyBy, sortBy, uniqBy, fromPairs } from 'lodash';
import { isAfter, endOfDay, format } from 'date-fns';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import qs from 'qs';

import firebase from '../firebase';
import { canUpdateTask } from '../shared/abilities';
import { taskFields, remindTimingHours, remindTimingLabel, hasUnreadComment } from '../shared/models/task';
import AddInTenantButton from './AddInTenantButton';
import ModelFormModal from './modals/ModelFormModal';
import TenantLink from './TenantLink';
import useTenant from './hooks/useTenant';
import useCollectionSubscriptionInTenant from './hooks/useCollectionSubscriptionInTenant';

const db = firebase.firestore();
const tasksRef = db.collection('tasks');
const usersRef = db.collection('users');
const activitiesRef = db.collection('activities');
const taskRemindTimingsRef = db.collection('taskRemindTimings');

export default function TaskButton(props) {
  const { user } = props;
  const location = useLocation();
  const tenant = useTenant();
  const taskItemRef = tasksRef.doc();

  const users = useCollectionSubscriptionInTenant(
    canUpdateTask(user) ? usersRef.where('role', 'in', ['admin', 'staff']) : null
  );
  const taskRemindTimings = sortBy(useCollectionSubscriptionInTenant(canUpdateTask(user) ? taskRemindTimingsRef : null), remindTimingHours);
  const assignedTasks = useCollectionSubscriptionInTenant(
    canUpdateTask(user) && user?.uid ? tasksRef.where('assignee.uid', '==', user.uid) : null,
    [user?.uid]
  );
  const createdTasks = useCollectionSubscriptionInTenant(
    canUpdateTask(user) && user?.uid ? tasksRef.where('addedBy.uid', '==', user.uid) : null,
    [user?.uid]
  );
  const myOpenTasks = assignedTasks.filter((_) => _.status === 'open');
  const openingTaskId = qs.parse(location.search.slice(1)).taskId;

  useEffect(() => {
    if (myOpenTasks.length === 0) return;

    const storageKey = `taskToastShownOn:${tenant?.id}:${user.uid}`;
    const today = format(new Date(), 'yyyy-MM-dd');
    try {
      if (localStorage.getItem(storageKey) === today) return;
      localStorage.setItem(storageKey, today);
    } catch (e) {
      return;
    }
    toast.info(`担当の未完了タスクが${myOpenTasks.length}件あります`, { autoClose: false });
  }, [myOpenTasks.length]);

  // 購読で後から届いたタスクは既存コメント扱いにし、把握済みのタスクに新しく付いたコメントだけトーストする
  const lastCommentedAtsRef = useRef({});
  const relatedTasks = uniqBy([...assignedTasks, ...createdTasks], 'id');
  const lastCommentedAtsSignature = relatedTasks.map((_) => `${_.id}:${_.lastCommentedAt?.toMillis() || 0}`).join(',');
  useEffect(() => {
    const prevLastCommentedAts = lastCommentedAtsRef.current;
    lastCommentedAtsRef.current = fromPairs(relatedTasks.map((_) => [_.id, _.lastCommentedAt?.toMillis() || 0]));

    relatedTasks
      .filter((_) => hasUnreadComment(_, user?.uid))
      .filter((_) => _.id !== openingTaskId)
      .filter((_) => _.id in prevLastCommentedAts && _.lastCommentedAt.toMillis() > prevLastCommentedAts[_.id])
      .forEach((task) => {
        toast.info(`${task.lastCommentedBy?.displayName || ''}さんが「${task.title}」にコメントしました`);
      });
  }, [lastCommentedAtsSignature]);

  if (!canUpdateTask(user)) return null;

  const unreadCommentTasks = relatedTasks.filter((_) => hasUnreadComment(_, user.uid));

  const usersByUid = keyBy(users, 'uid');
  const assigneeOptions = users.map((_) => ({ label: _.displayName, value: _.uid }));
  const remindTimingOptions = taskRemindTimings.map((_) => ({ label: remindTimingLabel(_), value: _.id }));

  const contextPath = location.pathname;

  const processValues = (values) => {
    const assigneeUser = values.assignee && usersByUid[values.assignee];
    return {
      title: values.title,
      description: values.description || null,
      assignee: assigneeUser ? pick(assigneeUser, ['uid', 'email', 'displayName']) : null,
      dueDate: values.dueDate || null,
      remindTimingIds: values.dueDate ? values.remindTimingIds || [] : [],
      status: 'open',
      // attachContext を外した場合は作成元を紐づけない
      context: values.attachContext ? { currentPath: contextPath } : null,
    };
  };

  const onCreated = async (values) => {
    await activitiesRef.add({
      ...(tenant && { tenantId: tenant.id }),
      type: 'createTask',
      payload: { taskId: taskItemRef.id, taskTitle: values.title },
      createdBy: user,
      createdAt: new Date(),
    });
  };

  // 赤=期限切れ＋今日期限（緊急）、グレー=それ以外の未完了（総量の目安）
  const todayEnd = endOfDay(new Date());
  const urgentCount = myOpenTasks.filter((_) => _.dueDate && !isAfter(_.dueDate.toDate(), todayEnd)).length;
  const otherCount = myOpenTasks.length - urgentCount;

  return (
    <div className="d-flex align-items-center">
      <TenantLink to="/admin/tasks" className="nav-link position-relative d-flex align-items-center" title="タスク一覧を開く">
        <span className="fas fa-tasks" />
        {urgentCount > 0 && (
          <span className="badge badge-pill badge-danger ml-1" title={`対応期限の来ているタスク ${urgentCount}件`}>
            {urgentCount}
          </span>
        )}
        {otherCount > 0 && (
          <span className="badge badge-pill badge-secondary ml-1" title={`その他の未完了タスク ${otherCount}件`}>
            {otherCount}
          </span>
        )}
        {unreadCommentTasks.length > 0 && (
          <span className="badge badge-pill badge-info ml-1" title={`未読コメントのあるタスク ${unreadCommentTasks.length}件`}>
            <span className="fas fa-comment mr-1" />
            {unreadCommentTasks.length}
          </span>
        )}
      </TenantLink>
      <AddInTenantButton
        color="link"
        size="sm"
        label={<span className="d-none d-md-inline">タスク追加</span>}
        title="新しいタスクを作成"
        icon={<span className="fas fa-plus mr-md-1" />}
        itemRef={taskItemRef}
        processValues={processValues}
        successMessage="タスクを作成しました"
        onFinish={onCreated}
        FormModal={ModelFormModal}
        initialValues={{ attachContext: true }}
        formProps={{
          title: 'タスク作成',
          submitLabel: '作成',
          fields: taskFields({ assigneeOptions, contextPath, remindTimingOptions }),
        }}
      />
    </div>
  );
}
